import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { SEO } from '../components/SEO';
import { Card } from '../components/ui/card';
import { Sun, Wind, Waves, TrendingUp, Zap } from 'lucide-react';

export const EnergyPotentialPage = () => {
  const { t, language } = useLanguage();

  return (
    <>
      <SEO 
        title={t('pages.potential.title')}
        description={t('pages.potential.intro')}
        path="/potencial-energetico"
      />

      <div className="pt-32 pb-24 bg-gradient-to-b from-white to-primary-50/30 min-h-screen">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16 space-y-4">
              <h1 className="text-5xl md:text-6xl font-bold gradient-text" data-testid="potential-page-title">
                {t('pages.potential.title')}
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto" data-testid="potential-page-intro">
                {t('pages.potential.intro')}
              </p>
            </div>

            {/* Energy Resources Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {[
                {
                  icon: Sun,
                  title: 'Solar',
                  value: '6.5 kWh/m²/día',
                  description: language === 'es' ? 'Irradiación solar abundante' : 'Abundant solar irradiation',
                  gradient: 'from-yellow-400 to-orange-500'
                },
                {
                  icon: Wind,
                  title: language === 'es' ? 'Viento' : 'Wind',
                  value: '9.7 m/s a 50m',
                  description: language === 'es' ? 'Vientos constantes' : 'Constant winds',
                  gradient: 'from-blue-400 to-cyan-500'
                },
                {
                  icon: Waves,
                  title: language === 'es' ? 'Oleaje' : 'Wave',
                  value: '2.5-4.0 m',
                  description: language === 'es' ? 'Potencial ola-motoríco' : 'Wave energy potential',
                  gradient: 'from-cyan-400 to-teal-500'
                }
              ].map((resource, idx) => (
                <Card key={idx} className="p-8 border-0 bg-gradient-to-br from-white to-slate-50 hover:shadow-xl transition-all">
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${resource.gradient} mb-6`}>
                    <resource.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{resource.title}</h3>
                  <div className="text-3xl font-bold text-primary-600 mb-3">{resource.value}</div>
                  <p className="text-slate-600">{resource.description}</p>
                </Card>
              ))}
            </div>

            {/* Key Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <Card className="p-6 bg-white">
                <div className="flex items-start space-x-4">
                  <TrendingUp className="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">
                      {language === 'es' ? 'Mayor Utilización' : 'Higher Utilization'}
                    </h3>
                    <p className="text-sm text-slate-600">
                      {language === 'es'
                        ? 'Complementariedad sol+viento permite operación 24/7 de electrolizadores'
                        : 'Sun+wind complementarity enables 24/7 electrolyzer operation'}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-white">
                <div className="flex items-start space-x-4">
                  <Zap className="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">
                      {language === 'es' ? 'Energía Estable' : 'Stable Energy'}
                    </h3>
                    <p className="text-sm text-slate-600">
                      {language === 'es'
                        ? 'Perfil de generación más predecible y confiable'
                        : 'More predictable and reliable generation profile'}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Disclaimer */}
            <Card className="p-6 bg-amber-50 border-amber-200" data-testid="potential-disclaimer">
              <p className="text-sm text-amber-900">
                <strong>Nota:</strong> {t('pages.potential.disclaimer')}
              </p>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};